import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

// User blocking (App Store 1.2). After a block the backend hides everything from that
// member; this event lets the open screens drop their content INSTANTLY as well.
export const USER_BLOCKED_EVENT = "yalola:user_blocked";

// Session deltas on top of user.blocked_ids (/auth/me) so realtime feeds (live chat,
// group chat) filter instantly without waiting for a profile refresh.
const sessionBlocked = new Set();
const sessionUnblocked = new Set();

export async function blockUser(userId, reason = "") {
  const { data } = await api.post(`/users/${userId}/block`, { reason });
  sessionBlocked.add(userId);
  sessionUnblocked.delete(userId);
  window.dispatchEvent(new CustomEvent(USER_BLOCKED_EVENT, { detail: { userId } }));
  return data;
}

export async function unblockUser(userId) {
  const { data } = await api.delete(`/users/${userId}/block`);
  sessionUnblocked.add(userId);
  sessionBlocked.delete(userId);
  return data;
}

export function useUserBlocked(onBlocked) {
  useEffect(() => {
    const on = (e) => onBlocked?.(e.detail?.userId);
    window.addEventListener(USER_BLOCKED_EVENT, on);
    return () => window.removeEventListener(USER_BLOCKED_EVENT, on);
  }, [onBlocked]);
}

// Set of member ids hidden from the signed-in user (blocked either way), live-updated.
export function useBlockedIds() {
  const { user } = useApp();
  const [tick, setTick] = useState(0);
  useUserBlocked(() => setTick((v) => v + 1));
  return useMemo(() => {
    const s = new Set([...(user?.blocked_ids || []), ...sessionBlocked]);
    sessionUnblocked.forEach((id) => s.delete(id));
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tick]);
}

// Drops every item authored by `userId` (posts, comments, stories groups…).
export const withoutAuthor = (list, userId) =>
  (list || []).filter((x) => x?.author_id !== userId && x?.author?.id !== userId && x?.user_id !== userId && x?.id !== userId);
